// ./src/audioBlock.ts
//
// The audio block's rules (ROADMAP A2d #72), pure — no React, no Mantine — so the app's `check-audio-block` RUNS them.

/** The page-wide "a block started playing" signal — every other audio block pauses (one sound at a time). */
export const AUDIO_PLAY_EVENT = 'em-audio-play';

export const MUSIC_VOLUME_DEFAULT = 80;
export const BACKGROUND_VOLUME_DEFAULT = 40;

/** A volume setting (0–100) as the element wants it (0–1); a missing or broken value falls back, out-of-range clamps. */
export const audioVolumeOf = (value: number | null | undefined, fallback: number): number => {
  const v = typeof value === 'number' && Number.isFinite(value) ? value : fallback;

  return Math.min(100, Math.max(0, v)) / 100;
};

/**
 * Should the block wait for the visitor's first tap to start? Only on the LIVE site (in the editor every click on the
 * canvas would start it), only when the host asked for it, only with music to play, never in the thumbnail, and only
 * while nothing has played yet.
 */
export const armsFirstTap = (p: { liveSite: boolean; autoplay: boolean; hasMusic: boolean; compact: boolean; idle: boolean }): boolean =>
  p.liveSite && p.autoplay && p.hasMusic && !p.compact && p.idle;

// ── Playback UI (A2d #72 follow-up, owner 2026-10-01: "nothing happens until a large file loads") ─────────────────

/**
 * What the player is doing. ⚠ `loading` is the state the owner asked for: Play pressed, the file not playing yet. Without
 * it a large file looks like a dead button for seconds. `buffering` is the same wait in the middle of a song.
 */
export type AudioPlayState = 'idle' | 'loading' | 'playing' | 'buffering' | 'paused' | 'blocked' | 'error';

/**
 * What happens to the player — every input that moves its state:
 *   press   — Play (or the first tap): the button answers at once;
 *   started — the browser really started the music (play() resolved);
 *   refused — the browser refused (NotAllowedError) while loading; `firstTap` → back to idle so the listener re-arms;
 *   cancel  — Play pressed again while loading;
 *   pause   — our Pause, or another block started;
 *   outsidePause — the ELEMENT paused from outside (lock screen, headset, a suspended audio context);
 *   waiting — a stall while playing;
 *   playing — the element plays: back from a stall, or resumed from OUTSIDE the block;
 *   ended   — the music finished (no repeat);
 *   failed  — the file cannot be played at all;
 *   reset   — a different track (the editor changed the link).
 */
export type AudioPlayEvent =
  | { type: 'press' }
  | { type: 'started' }
  | { type: 'refused'; firstTap: boolean }
  | { type: 'cancel' }
  | { type: 'pause' }
  | { type: 'outsidePause' }
  | { type: 'waiting' }
  | { type: 'playing' }
  | { type: 'ended' }
  | { type: 'failed' }
  | { type: 'reset' };

/**
 * The player's state machine — the ONE place its transitions live, so `check-audio-block` runs them instead of reading
 * the view's source (review, 2026-10-01). An event a state does not answer leaves it as it is.
 */
export const nextPlayState = (state: AudioPlayState, event: AudioPlayEvent): AudioPlayState => {
  switch (event.type) {
    case 'reset':
      return 'idle';
    case 'failed':
      return 'error';
    case 'press':
      return state === 'error' ? 'error' : 'loading';
    case 'started':
      return state === 'loading' ? 'playing' : state;
    case 'refused':
      return state === 'loading' ? (event.firstTap ? 'idle' : 'blocked') : state;
    case 'cancel':
      return state === 'loading' ? 'paused' : state;
    case 'pause':
      return state === 'loading' || state === 'playing' || state === 'buffering' ? 'paused' : state;
    case 'outsidePause':
      return state === 'playing' || state === 'buffering' ? 'paused' : state;
    case 'waiting':
      return state === 'playing' ? 'buffering' : state;
    case 'playing':
      // A start from our own Play is still `loading` — play() owns that one.
      return state === 'buffering' || state === 'paused' || state === 'idle' || state === 'blocked' ? 'playing' : state;
    case 'ended':
      return state === 'error' ? 'error' : 'paused';
    default:
      return state;
  }
};

/** True when `playing` came from OUTSIDE the block — it must then announce itself and bring the background back. */
export const isOutsideResume = (before: AudioPlayState, after: AudioPlayState): boolean =>
  after === 'playing' && (before === 'paused' || before === 'idle' || before === 'blocked');

/** A playback position as the player shows it — "m:ss", or "h:mm:ss" past an hour. Unknown or live (Infinity) → null. */
export const formatAudioTime = (seconds: number | null | undefined): string | null => {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) {
    return null;
  }

  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');

  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
};

/** The element's `buffered` ranges, as much of them as these rules read. */
export interface AudioRanges {
  length: number;
  start: (index: number) => number;
  end: (index: number) => number;
}

/**
 * How far the file is downloaded AHEAD of `at` — the end of the buffered range that holds the position. A range further
 * on (after a seek) does not count: playback cannot reach it without loading the gap. None holds it → `at` itself.
 */
export const bufferedEndAt = (ranges: AudioRanges | null | undefined, at: number): number => {
  if (!ranges) {
    return at;
  }

  for (let i = 0; i < ranges.length; i += 1) {
    if (ranges.start(i) <= at + 0.25 && at <= ranges.end(i)) {
      return ranges.end(i);
    }
  }

  return at;
};

/** "Loading… 35%" — the share of the file downloaded, when the length is known. Unknown → null (the bare "Loading…"). */
export const loadedPercent = (bufferedEnd: number, duration: number): number | null => {
  if (!Number.isFinite(duration) || duration <= 0 || !Number.isFinite(bufferedEnd)) {
    return null;
  }

  return Math.round(Math.min(100, Math.max(0, (bufferedEnd / duration) * 100)));
};

// ── Cross-origin: the visualizer and real volumes need it, and a file host may refuse it ─────────────────────────────

/**
 * How the element loads its file. `cors` (crossOrigin="anonymous") lets Web Audio READ the samples — the visualizer and
 * a volume that works on iPhones (iOS ignores `audio.volume`). A host that does not send CORS headers makes that load
 * FAIL outright, so the element is reloaded `plain`: it plays, and the block shows the simple playing animation.
 */
export type AudioSourceMode = 'cors' | 'plain';

/** A load error in `cors` mode is a retry without it; in `plain` mode the file really cannot be played (null). */
export const retryModeAfterError = (mode: AudioSourceMode): AudioSourceMode | null => (mode === 'cors' ? 'plain' : null);

// ── The visualizer ──────────────────────────────────────────────────────────────────────────────────────────────────

export type AudioVisualizer = 'bars' | 'wave' | 'none';
export const AUDIO_VISUALIZERS: AudioVisualizer[] = ['bars', 'wave', 'none'];
export const AUDIO_VISUALIZER_DEFAULT: AudioVisualizer = 'bars';

/** The stored setting, read safely — absent (every block saved before the setting existed) or unknown → bars. */
export const audioVisualizerOf = (value: unknown): AudioVisualizer =>
  AUDIO_VISUALIZERS.includes(value as AudioVisualizer) ? (value as AudioVisualizer) : AUDIO_VISUALIZER_DEFAULT;

/**
 * An analyser's frequency bins (0–255) as `count` bar heights (0–1). The low end gets the most bars — that is where
 * music lives — and the top quarter of the bins is skipped (mp3 cuts off there, so those bars would never move).
 * Every bar reads at least one bin; no bins → all zero.
 */
export const frequencyBars = (bins: ArrayLike<number>, count: number): number[] => {
  const usable = Math.floor(bins.length * 0.75);

  if (count <= 0) {
    return [];
  }

  if (usable <= 0) {
    return new Array(count).fill(0);
  }

  const bars: number[] = [];
  let from = 0;

  for (let i = 0; i < count; i += 1) {
    const edge = Math.round(usable * Math.pow((i + 1) / count, 2));
    const to = Math.min(usable, Math.max(from + 1, edge));
    let sum = 0;

    for (let b = from; b < to; b += 1) {
      sum += bins[Math.min(b, usable - 1)];
    }

    bars.push(Math.min(1, Math.max(0, sum / (to - from) / 255)));
    from = Math.min(usable - 1, to);
  }

  return bars;
};

/**
 * The fallback animation for a file whose samples cannot be read (its host refuses cross-origin access): a smooth,
 * deterministic movement — `t` in seconds — so the block still shows that it is playing. It is NOT the music's
 * spectrum, and the editor says so; it never claims to be.
 */
export const simulatedBars = (t: number, count: number): number[] => {
  const bars: number[] = [];

  for (let i = 0; i < count; i += 1) {
    const a = Math.sin(t * 2.1 + i * 0.7);
    const b = Math.sin(t * 3.7 + i * 1.3);
    const c = Math.sin(t * 1.3 + i * 0.4);

    bars.push(Math.min(1, Math.max(0.08, 0.45 + 0.22 * a + 0.18 * b + 0.12 * c)));
  }

  return bars;
};
